<script lang="ts">
	import { Dialog, Portal } from '@skeletonlabs/skeleton-svelte';

	type ModalLabels = {
		confirm: string;
		cancel: string;
		title: string;
	};

	type ModalClasses = {
		backdrop: string;
		positioner: string;
		content: string;
	};

	type ModalTypes = 'success' | 'error' | 'warning' | 'info';

	type MaybePromise<T> = T | Promise<T>;

	let {
		open = true,
		message,
		onConfirm,
		onCancel,
		labels,
		type,
		classes
	}: {
		open?: boolean;
		message: string;
		onConfirm: () => MaybePromise<void>;
		onCancel: () => void;
		labels: ModalLabels;
		type: ModalTypes;
		classes: ModalClasses;
	} = $props();

	const handleOpenChange = (details: { open: boolean }) => {
		if (!details.open) {
			onCancel();
		}
	};

	const btnClassByType = (type: ModalTypes) => {
		switch (type) {
			case 'success':
				return 'btn btn-success';
			case 'error':
				return 'btn btn-error btn-filled';
			case 'warning':
				return 'btn btn-warning';
			case 'info':
				return 'btn btn-info';
			default:
				return 'btn';
		}
	};

	// Usage example. Just like the old notifier:
	// confirmModal(
	//   'Er du sikker på at du vil publisere planen? Handlingen kan ikke angres',
	//   async () => {
	//     await postToApi();
	//   },
	//   () => {
	//     console.log('Cancelled');
	//   },
	//   {
	//     labels: {
	//       confirm: 'Publiser plan',
	//       cancel: 'Avbryt'
	//     }
	//   }
	// );
</script>

<Dialog role="alertdialog" {open} onOpenChange={handleOpenChange}>
	<Portal>
		<Dialog.Backdrop class={classes.backdrop} />

		<Dialog.Positioner class={classes.positioner}>
			<Dialog.Content class={classes.content}>
				<div class="space-y-3">
					<Dialog.Title class="text-xl font-semibold">{labels.title}</Dialog.Title>
					<Dialog.Description class="text-sm leading-6 text-surface-700-300">
						{@html message}
					</Dialog.Description>
				</div>

				<div class="flex justify-end gap-3 px-2 pt-4">
					<Dialog.CloseTrigger class="basis-1/2 btn btn-bordered">
						{labels.cancel}
					</Dialog.CloseTrigger>

					<button type="button" class="basis-1/2 {btnClassByType(type)}" onclick={onConfirm}>
						{labels.confirm}
					</button>
				</div>
			</Dialog.Content>
		</Dialog.Positioner>
	</Portal>
</Dialog>
